import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import { UserContext } from '../context/UserContext';

function clampInt(value, min, max) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

export default function EinarmigerBandit() {
  const { user } = useContext(UserContext);
  const isOwner = Array.isArray(user?.roles) && user.roles.includes('owner');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [spinning, setSpinning] = useState(false);
  const [reelStep, setReelStep] = useState([0, 0, 0]);
  const [result, setResult] = useState(null);
  const [pendingResult, setPendingResult] = useState(null);

  const [claiming, setClaiming] = useState(false);
  const [claimMsg, setClaimMsg] = useState('');
  const [claimErr, setClaimErr] = useState('');

  const [editItems, setEditItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveErr, setSaveErr] = useState('');

  const [spinCost, setSpinCost] = useState(0);
  const [editSpinCost, setEditSpinCost] = useState(0);
  const [costMsg, setCostMsg] = useState('');

  const [freeSpins, setFreeSpins] = useState({ daily: 0, bonus: 0, total: 0 });

  const [reelRepeat, setReelRepeat] = useState(20);
  const didAutoClaimRef = useRef(false);

  const reelItemHeight = 88;
  const reelWindowCount = 3;

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [cfgRes, meRes] = await Promise.all([
          api.get('/slot-machine/config'),
          api.get('/slot-machine/me'),
        ]);
        const data = cfgRes.data;
        const me = meRes.data;
        if (!mounted) return;
        const list = Array.isArray(data?.items) ? data.items : [];
        const sc = Number(data?.spinCost) || 0;
        setItems(list);
        setEditItems(list.map((it) => ({ ...it })));
        setSpinCost(sc);
        setEditSpinCost(sc);

        const fs = me?.freeSpins;
        setFreeSpins({
          daily: Number(fs?.daily) || 0,
          bonus: Number(fs?.bonus) || 0,
          total: Number(fs?.total) || 0,
        });
      } catch (e) {
        if (!mounted) return;
        setLoadError(e?.response?.data?.error || e.message || 'Fehler beim Laden');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const nextSpinNotice = useMemo(() => {
    const total = Number(freeSpins?.total) || 0;
    const daily = Number(freeSpins?.daily) || 0;
    const bonus = Number(freeSpins?.bonus) || 0;
    const cost = Number(spinCost) || 0;
    if (total > 0) {
      const kind = daily > 0 ? 'täglich' : (bonus > 0 ? 'Bonus' : 'Gratis');
      return {
        headline: 'Nächster Dreh: GRATIS',
        detail: kind === 'Bonus' ? `Quelle: ${kind} (Bonus: ${bonus})` : `Quelle: ${kind}`,
      };
    }
    if (cost <= 0) return { headline: 'Nächster Dreh: GRATIS', detail: 'Preis: 0 Wochenpunkte' };
    return { headline: `Nächster Dreh: kostet ${cost} Wochenpunkte`, detail: '' };
  }, [freeSpins, spinCost]);

  const legendEntries = useMemo(() => {
    const list = Array.isArray(items) ? items.slice(0, 7) : [];
    return list.map((it, i) => {
      const label = String(it?.label || `Feld ${i + 1}`).trim() || `Feld ${i + 1}`;
      const actionType = String(it?.actionType || 'none');
      if (actionType === 'points') {
        const n = Math.trunc(Number(it?.pointsDelta) || 0);
        const sign = n > 0 ? '+' : '';
        return { key: i, label, reward: `${sign}${n} Wochenpunkte` };
      }
      if (actionType === 'freeSpins') {
        const n = Math.max(0, Math.trunc(Number(it?.freeSpinsDelta) || 0));
        return { key: i, label, reward: `+${n} Gratisdrehungen` };
      }
      if (actionType === 'prize') {
        const prize = String(it?.prizeLabel || '').trim();
        return { key: i, label, reward: `Sachpreis: ${prize || '(ohne Name)'}` };
      }
      return { key: i, label, reward: 'Niete' };
    });
  }, [items]);

  const reelSequence = useMemo(() => {
    const n = items.length;
    const repeat = Math.max(6, Number(reelRepeat) || 0);
    const seq = [];
    if (!n) return seq;
    for (let r = 0; r < repeat; r++) {
      for (let i = 0; i < n; i++) {
        seq.push({ key: `${r}-${i}`, label: String(items[i]?.label || `Feld ${i + 1}`) });
      }
    }
    return seq;
  }, [items, reelRepeat]);

  const spin = async () => {
    if (spinning) return;
    if (!items.length) return;

    setSaveMsg('');
    setSaveErr('');
    setResult(null);
    setPendingResult(null);
    setClaimMsg('');
    setClaimErr('');
    setCostMsg('');
    didAutoClaimRef.current = false;

    setSpinning(true);

    try {
      const { data } = await api.post('/slot-machine/spin');
      const turns = clampInt(data?.turns, 1, 120);
      const spinId = typeof data?.spinId === 'string' ? data.spinId : '';

      const reels = Array.isArray(data?.reels) ? data.reels : null;
      const idx0 = clampInt(reels?.[0]?.index, 0, items.length - 1);
      const idx1 = clampInt(reels?.[1]?.index, 0, items.length - 1);
      const idx2 = clampInt(reels?.[2]?.index, 0, items.length - 1);

      const usedFree = typeof data?.usedFreeSpin === 'string' ? data.usedFreeSpin : null;
      const costApplied = Number(data?.costApplied) || 0;
      if (usedFree) setCostMsg('Gratisdrehung verwendet');
      else if (costApplied > 0) setCostMsg(`Preis: -${costApplied} Wochenpunkte`);

      const fs = data?.freeSpins;
      if (fs) {
        setFreeSpins({ daily: Number(fs?.daily) || 0, bonus: Number(fs?.bonus) || 0, total: Number(fs?.total) || 0 });
      }

      // Ensure the sequence is long enough for the target step index.
      setReelRepeat(Math.max(40, turns + 20));

      const n = items.length;
      const middleOffset = 1; // 3-row window: middle is 1 item below the top
      // Stagger reels by adding full rotations so they stop one after another.
      setReelStep([
        turns * n + idx0 - middleOffset,
        (turns + 2) * n + idx1 - middleOffset,
        (turns + 4) * n + idx2 - middleOffset,
      ]);

      setPendingResult({
        reels: [
          { index: idx0, label: reels?.[0]?.label || items[idx0]?.label || `Feld ${idx0 + 1}` },
          { index: idx1, label: reels?.[1]?.label || items[idx1]?.label || `Feld ${idx1 + 1}` },
          { index: idx2, label: reels?.[2]?.label || items[idx2]?.label || `Feld ${idx2 + 1}` },
        ],
        // Server payout is tied to the middle reel (same mechanics as Glücksrad)
        index: idx1,
        label: reels?.[1]?.label || data?.label || items[idx1]?.label || `Feld ${idx1 + 1}`,
        spinId,
      });
    } catch (e) {
      alert(e?.response?.data?.error || e.message || 'Drehen fehlgeschlagen');
      setSpinning(false);
    }
  };

  const claim = async (spinId) => {
    if (!spinId) return;
    setClaiming(true);
    setClaimMsg('');
    setClaimErr('');
    try {
      const { data } = await api.post('/slot-machine/claim', { spinId });
      const action = data?.action;
      if (action?.type === 'points') {
        const n = Number(action?.pointsDelta) || 0;
        const sign = n > 0 ? '+' : '';
        setClaimMsg(`Punkte: ${sign}${n}`);
      } else if (action?.type === 'freeSpins') {
        const n = Number(action?.freeSpinsDelta) || 0;
        setClaimMsg(`Gratisdrehungen: +${n}`);
      } else if (action?.type === 'prize') {
        setClaimMsg(`Sachpreis gewonnen: ${action?.label || ''}`);
      } else {
        setClaimMsg('Niete');
      }

      const fs = data?.freeSpins;
      if (fs) {
        setFreeSpins({ daily: Number(fs?.daily) || 0, bonus: Number(fs?.bonus) || 0, total: Number(fs?.total) || 0 });
      }
    } catch (err) {
      setClaimErr(err?.response?.data?.error || err.message || 'Aktion fehlgeschlagen');
    } finally {
      setClaiming(false);
    }
  };

  const onReelTransitionEnd = async (e) => {
    if (e?.propertyName && e.propertyName !== 'transform') return;
    if (!spinning) return;

    // Only trigger once, and only when the last reel finished.
    if (didAutoClaimRef.current) return;
    didAutoClaimRef.current = true;

    setSpinning(false);

    const finalResult = pendingResult;
    setResult((prev) => prev || finalResult);
    setPendingResult(null);

    const spinId = typeof finalResult?.spinId === 'string' ? finalResult.spinId : '';
    if (!spinId) return;
    await claim(spinId);
  };

  const saveWeights = async () => {
    if (!isOwner) return;
    setSaving(true);
    setSaveMsg('');
    setSaveErr('');
    try {
      const payload = {
        spinCost: clampInt(editSpinCost, 0, 1000000),
        items: (editItems || []).map((it, i) => ({
          label: String(it?.label || `Feld ${i + 1}`).trim(),
          weight: clampInt(it?.weight, 0, 1000000),
          actionType: it?.actionType === 'points'
            ? 'points'
            : (it?.actionType === 'freeSpins'
              ? 'freeSpins'
              : (it?.actionType === 'prize' ? 'prize' : 'none')),
          pointsDelta: it?.actionType === 'points' ? Math.trunc(Number(it?.pointsDelta) || 0) : null,
          freeSpinsDelta: it?.actionType === 'freeSpins' ? clampInt(it?.freeSpinsDelta, 0, 1000000) : null,
          prizeLabel: it?.actionType === 'prize' ? String(it?.prizeLabel || '').trim() : null,
        })),
      };
      const { data } = await api.put('/slot-machine/config', payload);
      const list = Array.isArray(data?.items) ? data.items : payload.items;
      const sc = Number(data?.spinCost);
      setItems(list);
      setEditItems(list.map((it) => ({ ...it })));
      if (Number.isFinite(sc)) {
        setSpinCost(sc);
        setEditSpinCost(sc);
      }
      setSaveMsg(data?.message || 'Gespeichert');
    } catch (e) {
      setSaveErr(e?.response?.data?.error || e.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  // Make the slot feel significantly longer than the wheel.
  const reelDuration = [8.7, 10.2, 11.7];

  return (
    <div className="home-page" style={{ position: 'relative' }}>
      {/* Hero header */}
      <section style={{ width: '100vw', margin: 0, padding: 0, position: 'relative', left: '-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>
          Einarmiger Bandit
        </h2>
        <p className="hero-desc" style={{ margin: '0 auto 24px', textAlign: 'center', maxWidth: 760 }}>
          Dreh an den Walzen und erhalte zufällige Gewinne.
        </p>
      </section>

      {/* Centered content */}
      <section
        style={{
          width: '100%',
          maxWidth: 900,
          margin: '0 auto',
          padding: '0 16px 40px 16px',
          color: '#fff',
        }}
      >
        {loading && <div style={{ color: '#ccc' }}>Lade Einarmiger Bandit…</div>}
        {!loading && loadError && <div style={{ color: '#ffb3b3' }}>{loadError}</div>}

        {!loading && !loadError && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              {/* Slot window */}
              <div
                style={{
                  width: 'min(520px, 92vw)',
                  borderRadius: 14,
                  border: '1px solid rgba(255,255,255,0.18)',
                  background: 'rgba(0,0,0,0.35)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
                  padding: 14,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
                  {[0, 1, 2].map((reelIdx) => (
                    <div
                      key={reelIdx}
                      style={{
                        width: 'calc((min(520px, 92vw) - 48px) / 3)',
                        maxWidth: 160,
                        minWidth: 90,
                        height: reelItemHeight * reelWindowCount,
                        overflow: 'hidden',
                        borderRadius: 10,
                        border: '1px solid rgba(255,255,255,0.16)',
                        background: 'rgba(0,0,0,0.25)',
                        position: 'relative',
                      }}
                    >
                      {/* Payline */}
                      <div
                        aria-hidden
                        style={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: reelItemHeight,
                          height: reelItemHeight,
                          borderTop: '1px solid rgba(255,255,255,0.25)',
                          borderBottom: '1px solid rgba(255,255,255,0.25)',
                          background: 'rgba(255,255,255,0.05)',
                          pointerEvents: 'none',
                        }}
                      />

                      <div
                        style={{
                          transform: `translateY(${-reelStep[reelIdx] * reelItemHeight}px)`,
                          transition: spinning
                            ? `transform ${reelDuration[reelIdx]}s cubic-bezier(0.12, 0.85, 0.12, 1)`
                            : 'none',
                          userSelect: 'none',
                        }}
                        onTransitionEnd={reelIdx === 2 ? onReelTransitionEnd : undefined}
                      >
                        {reelSequence.map((entry) => (
                          <div
                            key={`${reelIdx}-${entry.key}`}
                            style={{
                              height: reelItemHeight,
                              display: 'grid',
                              placeItems: 'center',
                              color: 'rgba(255,255,255,0.95)',
                              textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                              fontWeight: 800,
                              fontSize: 24,
                              padding: '0 10px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={entry.label}
                          >
                            {entry.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Prominent next-spin notice */}
              <div
                style={{
                  width: '100%',
                  maxWidth: 520,
                  marginTop: 10,
                  padding: '12px 14px',
                  borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.22)',
                  background: 'rgba(0,0,0,0.35)',
                  boxShadow: '0 10px 24px rgba(0,0,0,0.35)',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 900, color: 'rgba(255,255,255,0.98)', letterSpacing: 0.2 }}>
                  {nextSpinNotice.headline}
                </div>
                {nextSpinNotice.detail && (
                  <div style={{ marginTop: 4, fontSize: 12, color: '#ccc' }}>{nextSpinNotice.detail}</div>
                )}
                <div style={{ marginTop: 6, fontSize: 12, color: '#999' }}>
                  Hinweis: Die tägliche Gratisdrehung verfällt, wenn du sie heute nicht nutzt.
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button onClick={spin} disabled={spinning || !items.length}>
                  {spinning ? 'Dreht…' : 'Drehen'}
                </button>
                {result && (
                  <div style={{ color: '#fff' }}>
                    Ergebnis:{' '}
                    <b>
                      {Array.isArray(result?.reels)
                        ? result.reels.map((r) => r?.label || '').filter(Boolean).join(' | ')
                        : (result.label || '')}
                    </b>
                  </div>
                )}
              </div>

              {/* Legend */}
              {legendEntries.length > 0 && (
                <div
                  style={{
                    width: '100%',
                    maxWidth: 520,
                    marginTop: 10,
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.18)',
                    background: 'rgba(0,0,0,0.28)',
                    boxShadow: '0 10px 24px rgba(0,0,0,0.28)',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 8, color: 'rgba(255,255,255,0.95)' }}>
                    Legende (Gewinn nur bei 3 gleichen Symbolen)
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                    {legendEntries.map((entry, i) => (
                      <div
                        key={entry.key}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 10,
                          fontSize: 12,
                          color: '#ccc',
                        }}
                      >
                        <div style={{ fontWeight: 800, color: 'rgba(255,255,255,0.9)' }}>
                          {entry.label} | {entry.label} | {entry.label}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          {entry.reward}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ marginTop: 6, color: '#ccc', fontSize: 12, textAlign: 'center' }}>
                Gratisdrehungen: <b>{Number(freeSpins?.total) || 0}</b> (täglich: {Number(freeSpins?.daily) || 0}, Bonus: {Number(freeSpins?.bonus) || 0})
              </div>

              {(claiming || claimMsg || claimErr) && (
                <div style={{ marginTop: 6, color: '#ccc', fontSize: 12, textAlign: 'center' }}>
                  {claiming && 'Aktion wird angewendet…'}
                  {!claiming && claimMsg && <span style={{ color: '#c8ffc8' }}>{claimMsg}</span>}
                  {!claiming && claimErr && <span style={{ color: '#ffb3b3' }}>{claimErr}</span>}
                </div>
              )}

              {spinning && costMsg && (
                <div style={{ marginTop: 6, color: '#c8ffc8', fontSize: 12, textAlign: 'center' }}>{costMsg}</div>
              )}

              <div style={{ width: '100%', maxWidth: 520, color: '#ccc', fontSize: 12, textAlign: 'center' }}>
                Das Ergebnis wird serverseitig zufällig ermittelt.
              </div>
            </div>

            {/* Owner: probability config */}
            {isOwner && (
              <div style={{ marginTop: 28, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                <h3 style={{ margin: '0 0 10px' }}>Owner: Wahrscheinlichkeiten</h3>
                <div style={{ color: '#ccc', fontSize: 12, marginBottom: 12 }}>
                  Gewichte &gt; 0 erhöhen die Wahrscheinlichkeit des jeweiligen Felds (relative Gewichtung).
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, maxWidth: 740 }}>
                  <div style={{ color: '#ccc', fontSize: 12, minWidth: 170 }}>Preis pro Dreh (Punkte):</div>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={Number.isFinite(Number(editSpinCost)) ? editSpinCost : 0}
                    onChange={(e) => setEditSpinCost(e.target.value)}
                    style={{ width: 140 }}
                  />
                  <div style={{ color: '#999', fontSize: 12 }}>
                    Aktuell: {Number(spinCost) || 0}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 110px 140px', gap: 8, maxWidth: 740 }}>
                  {(editItems || []).map((it, i) => (
                    <div key={i} style={{ display: 'contents' }}>
                      <div>
                        <input
                          type="text"
                          value={typeof it?.label === 'string' ? it.label : ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setEditItems((prev) => {
                              const next = [...(prev || [])];
                              next[i] = { ...next[i], label: v };
                              return next;
                            });
                          }}
                          placeholder={`Feld ${i + 1}`}
                          style={{ width: '100%' }}
                        />
                      </div>

                      <div>
                        <select
                          value={it?.actionType === 'points' ? 'points' : (it?.actionType === 'freeSpins' ? 'freeSpins' : (it?.actionType === 'prize' ? 'prize' : 'none'))}
                          onChange={(e) => {
                            const v = e.target.value;
                            setEditItems((prev) => {
                              const next = [...(prev || [])];
                              next[i] = {
                                ...next[i],
                                actionType: v === 'points' ? 'points' : (v === 'freeSpins' ? 'freeSpins' : (v === 'prize' ? 'prize' : 'none')),
                                pointsDelta: v === 'points' ? (Number(next[i]?.pointsDelta) || 0) : null,
                                freeSpinsDelta: v === 'freeSpins' ? (Number(next[i]?.freeSpinsDelta) || 1) : null,
                                prizeLabel: v === 'prize' ? (typeof next[i]?.prizeLabel === 'string' ? next[i].prizeLabel : '') : null,
                              };
                              return next;
                            });
                          }}
                          style={{ width: 90 }}
                        >
                          <option value="none">Niete</option>
                          <option value="points">Punkte</option>
                          <option value="freeSpins">Gratis</option>
                          <option value="prize">Sachpreis</option>
                        </select>
                      </div>

                      <div>
                        <input
                          type={it?.actionType === 'prize' ? 'text' : 'number'}
                          step={it?.actionType === 'prize' ? undefined : 1}
                          min={it?.actionType === 'prize' ? undefined : 0}
                          value={
                            it?.actionType === 'prize'
                              ? (typeof it?.prizeLabel === 'string' ? it.prizeLabel : '')
                              : (it?.actionType === 'freeSpins'
                                ? (Number.isFinite(Number(it?.freeSpinsDelta)) ? it.freeSpinsDelta : 1)
                                : (Number.isFinite(Number(it?.pointsDelta)) ? it.pointsDelta : 0))
                          }
                          disabled={it?.actionType !== 'points' && it?.actionType !== 'freeSpins' && it?.actionType !== 'prize'}
                          onChange={(e) => {
                            const v = e.target.value;
                            setEditItems((prev) => {
                              const next = [...(prev || [])];
                              if (next[i]?.actionType === 'prize') next[i] = { ...next[i], prizeLabel: v };
                              else if (next[i]?.actionType === 'freeSpins') next[i] = { ...next[i], freeSpinsDelta: v };
                              else next[i] = { ...next[i], pointsDelta: v };
                              return next;
                            });
                          }}
                          placeholder={it?.actionType === 'prize' ? 'Preisname' : undefined}
                          style={{ width: 110 }}
                        />
                      </div>

                      <div>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={Number.isFinite(Number(it?.weight)) ? it.weight : 0}
                          onChange={(e) => {
                            const v = e.target.value;
                            setEditItems((prev) => {
                              const next = [...(prev || [])];
                              next[i] = { ...next[i], weight: v };
                              return next;
                            });
                          }}
                          style={{ width: 120 }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button onClick={saveWeights} disabled={saving}>
                    {saving ? 'Speichert…' : 'Wahrscheinlichkeiten speichern'}
                  </button>
                  {saveMsg && <div style={{ color: '#c8ffc8' }}>{saveMsg}</div>}
                  {saveErr && <div style={{ color: '#ffb3b3' }}>{saveErr}</div>}
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
